import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface GradientHeroProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  decoration?: ReactNode;
  className?: string;
}

export function GradientHero({
  eyebrow,
  title,
  subtitle,
  children,
  decoration,
  className,
}: GradientHeroProps) {
  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-[20px] border border-line-1 bg-bg-1 shadow-card',
        className,
      )}
    >
      <div className="hero-gradient absolute inset-0" aria-hidden />
      <div className="relative grid grid-cols-1 gap-6 px-5 py-6 sm:px-7 sm:py-8 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex min-w-0 flex-col justify-between gap-4">
          {eyebrow && <div className="text-[13px] text-text-2">{eyebrow}</div>}
          <h2 className="font-display text-[28px] leading-tight tracking-tight text-text-1 sm:text-hero">
            {title}
          </h2>
          {subtitle && <p className="max-w-xl text-[14px] text-text-2">{subtitle}</p>}
          {children && <div className="mt-1">{children}</div>}
        </div>
        {decoration && <div className="relative hidden md:block">{decoration}</div>}
      </div>
    </section>
  );
}

export function HeroStarburst({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={cn('h-40 w-40 drop-shadow-[0_8px_24px_hsl(260_90%_50%/0.15)]', className)}
    >
      <defs>
        <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(0 0% 100%)" />
          <stop offset="50%" stopColor="hsl(268 70% 88%)" />
          <stop offset="100%" stopColor="hsl(240 40% 72%)" />
        </linearGradient>
        <linearGradient id="starGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(0 0% 100%)" />
          <stop offset="100%" stopColor="hsl(268 50% 75%)" />
        </linearGradient>
      </defs>
      <g transform="translate(100 100)">
        <path
          d="M0 -80 Q 14 -14 80 0 Q 14 14 0 80 Q -14 14 -80 0 Q -14 -14 0 -80 Z"
          fill="url(#starGrad)"
          opacity="0.95"
        />
        <path
          d="M0 -40 Q 8 -8 40 0 Q 8 8 0 40 Q -8 8 -40 0 Q -8 -8 0 -40 Z"
          fill="url(#starGrad2)"
          transform="translate(46 -14) scale(0.55)"
        />
      </g>
    </svg>
  );
}
